import './StarsInput.scss'
import { addEventListener } from '../../utils/events'

import {
  InternalComponentAPI,
  InternalPluginProps,
  Rate
} from '../../utils/types'
import { withClass } from '../../utils/selectors'
import { isValidRate } from '../../utils/ratings'
import { toggleChildClass } from '../../utils/dom'

const STARS_CLASS = 'star-input'
const RATING_VALUE_DATA_ATTRIB = 'data-rating-value'

type StarsInputProps = InternalPluginProps & {
  target: Element
  onRatingChange?(rating: number): void
}

/**
 * Rating input component API
 */
export type StarsInputAPI = Pick<InternalComponentAPI, 'destroy'> & {
  getRating(): Rate | null
  setDisabled: (disabled: boolean) => void
}

/**
 * Rating input component
 */
const StarsInput = ({
  target,
  onRatingChange,
  ...props
}: StarsInputProps): StarsInputAPI => {
  // Build HTML & child components
  target.innerHTML = starsComponent(props)
  const stars = target.querySelector(withClass(STARS_CLASS))!
  let isDisabled = false

  // Event listners & handlers
  const onStarClick = (event: Event) => {
    if (isDisabled) {
      return
    }
    const rating =
      (event.target as Element).getAttribute(RATING_VALUE_DATA_ATTRIB) || '0'
    stars.setAttribute(RATING_VALUE_DATA_ATTRIB, rating)
    onRatingChange && onRatingChange(parseInt(rating))
  }

  addEventListener({
    target: stars,
    eventName: 'click',
    selector: 'button',
    handler: onStarClick
  })

  // Public API
  return {
    getRating: () => {
      const rate = parseInt(
        stars.getAttribute(RATING_VALUE_DATA_ATTRIB) || '0',
        10
      )
      return isValidRate(rate) ? rate : null
    },
    setDisabled: (disabled: boolean) => {
      isDisabled = disabled
      toggleChildClass({
        parent: target,
        selector: withClass(STARS_CLASS),
        className: 'is-disabled',
        classPresent: disabled
      })
    },
    destroy: () => {
      target.innerHTML = ''
    }
  }
}

export default StarsInput

//
// Templates
//
const starsComponent = (props: InternalPluginProps) => `
  <div class="${STARS_CLASS}" ${RATING_VALUE_DATA_ATTRIB}="0">
    <button ${RATING_VALUE_DATA_ATTRIB}="1" tabindex="-1" onfocus="this.blur();"><span class="star-input__description">${props.translations.rating1}</span></button>
    <button ${RATING_VALUE_DATA_ATTRIB}="2" tabindex="-1" onfocus="this.blur();"><span class="star-input__description">${props.translations.rating2}</span></button>
    <button ${RATING_VALUE_DATA_ATTRIB}="3" tabindex="-1" onfocus="this.blur();"><span class="star-input__description">${props.translations.rating3}</span></button>
    <button ${RATING_VALUE_DATA_ATTRIB}="4" tabindex="-1" onfocus="this.blur();"><span class="star-input__description">${props.translations.rating4}</span></button>
    <button ${RATING_VALUE_DATA_ATTRIB}="5"><span class="star-input__description">${props.translations.rating5}</span></button>
  </div>
`
